import isEmpty from "lodash/isEmpty";
import css from "PFApp/activities/show/parts/activity_profiles/add_activity_profiles_modal_content.module.scss";
import { ProfileItem } from "PFApp/components/profile_item";
import { buildProfilesOptionsFetcher } from "PFApp/helpers/options/profiles_options";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Select } from "PFComponents/select";
import api from "PFCore/api";
import { getProfileName } from "PFCore/helpers/profile";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { useEngagementsInvalidate } from "PFCore/hooks/queries/engagements/use_engagements_invalidate";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useRolesInvalidate } from "PFCore/hooks/queries/roles/use_roles_invalidate";
import { Activity, BasicProfile, FeatureFlag, Profile } from "PFTypes";
import { Option } from "PFTypes/options";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useTemplate } from "../../../../hooks";
import { isEngagement, isRole } from "../../../helpers";
import { UseAddActivityProfilesReturn } from "./activity_profiles.types";

export const useAddAssigneeModal = ({ activity }: { activity: Activity }): UseAddActivityProfilesReturn => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("activities");
  const { t: tTranslation } = useTranslation();
  const { invalidate: invalidateActivities } = useActivityInvalidate();
  const { invalidateRoles } = useRolesInvalidate();
  const { invalidateAll: invalidateEngagements } = useEngagementsInvalidate();

  const { showGrowl } = useGrowlStoreActions();
  const isEnabled = useIsFeatureEnabled();
  const { getFirstError } = useResponseErrors();

  const template = useTemplate(activity?.template_id);

  const activityAssignee = !isEmpty(activity.assignee?.profile) ? activity.assignee : null;
  const [assignee, setAssignee] = useState(activityAssignee?.profile);

  const description = (
    <div>
      <Trans i18nKey={"show.parts.changeAssigneeDescription"} t={t} components={[<strong key="0" />]} />
    </div>
  );

  useEffect(() => {
    setAssignee(activityAssignee?.profile);
  }, [activityAssignee?.profile]);

  const handleReset = () => {
    setAssignee(activityAssignee?.profile);
  };

  const handleSubmit = () => {
    if (!assignee) {
      if (activityAssignee) {
        return api({
          url: `assignees/${activityAssignee.id}`,
          method: "DELETE"
        })
          .then(() => {
            isRole(template?.key) && invalidateRoles();
            isEngagement(template?.key) && invalidateEngagements();
            invalidateActivities([activity.id]).then(() => {
              showGrowl({
                kind: "success",
                message: t("show.parts.assigneeRemoved")
              });
            });
          })
          .catch(() => {
            showGrowl({
              kind: "error",
              message: t("show.parts.unableToRemoveAssignee")
            });
          });
      }

      return Promise.resolve();
    }

    return api({
      url: "assignees",
      method: "POST",
      data: {
        profileId: assignee.id,
        activityId: activity.id
      }
    })
      .then(() => {
        isRole(template?.key) && invalidateRoles();
        isEngagement(template?.key) && invalidateEngagements();
        invalidateActivities([activity.id]).then(() => {
          showGrowl({
            message: t("show.parts.profileAssigned", { profileName: getProfileName(assignee) }),
            kind: "success"
          });
        });
      })
      .catch((error) => {
        const errorMessage = getFirstError(error);
        showGrowl({
          kind: "error",
          message:
            errorMessage || t("show.parts.profileNotAssigned", { profileName: getProfileName(assignee) })
        });
      });
  };

  const getModalContent = () => (
    <>
      <Select<BasicProfile>
        qaId="ManageParticipants.AssigneeSelect"
        placeholder={tTranslation("search")}
        renderDisplayValue={() => (assignee ? <ProfileItem profile={assignee} /> : null)}
        value={assignee?.id}
        fetchOptions={buildProfilesOptionsFetcher({
          policyName: "resourcer",
          profileVisibility: "active",
          excludeProfileId: activity.coowned ? undefined : currentProfile.id,
          getValue: (profile) => String(profile.id)
        })}
        onClear={
          isEnabled(FeatureFlag.RequireAssignee) || !assignee ? undefined : () => setAssignee(undefined)
        }
        onChange={(option: Option<BasicProfile>) => setAssignee(option.original as Profile)}
        multiple={false}
      />
      {description && (
        <div className={css.subtitle}>
          <strong>{tTranslation("pleaseNote")}</strong>
          <br />
          <div className={css.description}>{description}</div>
        </div>
      )}
    </>
  );

  return { getModalContent, handleSubmit, enableSubmit: true, handleReset };
};
